/**
 * ManageSkuUomsDialog.jsx — Edit a SKU's UOM conversion list.
 *
 * Opened from Inventory row → "UOMs" button. Lets admins add/remove pack-size
 * conversions (e.g. for an oil SKU stocked in mL: add "L" with factor 1000).
 *
 * Props:
 *   open, onOpenChange
 *   sku       The SKU row {id, sku_code, name, unit_of_measure, uom_conversions, allow_fractional}
 *   onSaved   () => void — parent re-fetches SKUs
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { addSkuUom, removeSkuUom, getSkuUoms } from '../lib/api';

export default function ManageSkuUomsDialog({ open, onOpenChange, sku, onSaved }) {
  const [rows, setRows] = useState([]); // [{code, factor, label, is_base}]
  const [draft, setDraft] = useState({ code: '', factor: '', label: '', unit_price: '' });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    if (!sku?.id) return;
    setLoading(true);
    try {
      const r = await getSkuUoms(sku.id);
      setRows(r.data.uoms || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load UOMs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && sku?.id) load();
  }, [open, sku?.id]);

  const handleAdd = async () => {
    const code = (draft.code || '').trim().toLowerCase();
    const factor = parseFloat(draft.factor);
    if (!code) { toast.error('Enter a UOM code (e.g. L, case)'); return; }
    if (!Number.isFinite(factor) || factor <= 0) {
      toast.error('Factor must be a positive number');
      return;
    }
    const price = parseFloat(draft.unit_price);
    const body = { code, factor, label: draft.label.trim() || null };
    if (Number.isFinite(price) && price >= 0) body.unit_price = price;
    setSubmitting(true);
    try {
      await addSkuUom(sku.id, body);
      setDraft({ code: '', factor: '', label: '', unit_price: '' });
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add UOM');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (code) => {
    if (!window.confirm(`Remove UOM "${code}" from this SKU?`)) return;
    try {
      await removeSkuUom(sku.id, code);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove UOM');
    }
  };

  const baseRow = rows.find((r) => r.is_base);
  const convRows = rows.filter((r) => !r.is_base);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-xl" data-testid="manage-uoms-dialog">
        <DialogHeader>
          <DialogTitle>
            UOMs — <span className="font-mono">{sku?.sku_code}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="bg-muted/30 border border-border px-3 py-2">
            <div className="overline">Base unit</div>
            <div className="mt-1 font-mono">
              {baseRow?.code || sku?.unit_of_measure || 'each'}
              <span className="ml-2 text-[11px] text-muted-foreground normal-case">
                (factor 1 · always available)
              </span>
            </div>
          </div>

          <div>
            <div className="overline mb-2">Pack-size conversions</div>
            {loading ? (
              <p className="text-xs opacity-60">Loading…</p>
            ) : convRows.length === 0 ? (
              <p className="text-xs opacity-60">
                No pack sizes yet. Add one below — e.g. for oil SKU with base <span className="font-mono">mL</span>,
                add <span className="font-mono">L</span> with factor 1000.
              </p>
            ) : (
              <table className="w-full text-xs border border-border" data-testid="uom-rows">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-2 py-1">Code</th>
                    <th className="text-right px-2 py-1">Factor</th>
                    <th className="text-right px-2 py-1">Price</th>
                    <th className="text-left px-2 py-1">Label</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {convRows.map((r) => (
                    <tr key={r.code} className="border-t border-border" data-testid={`uom-row-${r.code}`}>
                      <td className="px-2 py-1 font-mono uppercase">{r.code}</td>
                      <td className="px-2 py-1 text-right font-mono">{r.factor}</td>
                      <td className="px-2 py-1 text-right font-mono" data-testid={`uom-row-${r.code}-price`}>
                        {r.unit_price != null ? `$${Number(r.unit_price).toFixed(2)}` : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-1 text-muted-foreground">{r.label || '—'}</td>
                      <td className="px-2 py-1 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemove(r.code)}
                          className="text-muted-foreground hover:text-red-600"
                          aria-label={`Remove ${r.code}`}
                          data-testid={`uom-remove-${r.code}`}
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="border-t border-border pt-3">
            <div className="overline mb-2">Add a UOM</div>
            <div className="grid grid-cols-[1fr_1fr_1fr_1.5fr_auto] gap-2 items-end">
              <div>
                <Label className="overline">Code</Label>
                <Input
                  value={draft.code}
                  onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                  placeholder="L"
                  className="mt-1 font-mono uppercase"
                  maxLength={24}
                  data-testid="uom-add-code"
                />
              </div>
              <div>
                <Label className="overline">Factor</Label>
                <Input
                  type="number" min={0} step="any"
                  value={draft.factor}
                  onChange={(e) => setDraft({ ...draft, factor: e.target.value })}
                  placeholder="1000"
                  className="mt-1 font-mono"
                  data-testid="uom-add-factor"
                />
              </div>
              <div>
                <Label className="overline">Price ($)</Label>
                <Input
                  type="number" min={0} step="0.01"
                  value={draft.unit_price}
                  onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
                  placeholder="optional"
                  className="mt-1 font-mono"
                  data-testid="uom-add-price"
                />
              </div>
              <div>
                <Label className="overline">Label (optional)</Label>
                <Input
                  value={draft.label}
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                  placeholder="case of 12×1L"
                  className="mt-1"
                  maxLength={120}
                  data-testid="uom-add-label"
                />
              </div>
              <Button
                type="button" onClick={handleAdd}
                disabled={submitting}
                className="bg-brand hover:bg-brand/90"
                data-testid="uom-add-btn"
              >
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Factor = how many base units (<span className="font-mono">{baseRow?.code || 'each'}</span>) equal 1 of this UOM.
              Price is the customer-facing $ per 1 of this UOM (leave blank to auto-derive from base price × factor).
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
